/**
 * Preferencias de acessibilidade da pessoa.
 *
 * Elas viram atributos `data-*` no <html>, e os componentes nunca leem a
 * preferencia diretamente: o CSS remapeia TOKEN (--color-text, --color-border-control).
 * Assim contraste alto nao duplica nenhum componente -- se duplicasse, um dos
 * dois ficaria para tras na primeira mudanca de layout.
 */
export const CONTRASTES = ['padrao', 'alto'] as const;
export const FONTES = ['padrao', 'grande', 'muito_grande'] as const;
export const MOVIMENTOS = ['padrao', 'reduzido'] as const;
/**
 * Voz no chat da Ana (ADR 0016). Não vira `data-*`: só o chat lê.
 * `ler` lê cada resposta em voz alta; `conversa` também abre o microfone ao
 * abrir a Ana e volta a ouvir depois de cada resposta.
 */
export const VOZES = ['normal', 'ler', 'conversa'] as const;

export type Contraste = (typeof CONTRASTES)[number];
export type Fonte = (typeof FONTES)[number];
export type Movimento = (typeof MOVIMENTOS)[number];
export type Voz = (typeof VOZES)[number];

export interface PreferenciasA11y {
  contraste: Contraste;
  fonte: Fonte;
  movimento: Movimento;
  /** Opcional: espelhos antigos no aparelho não têm. Ausente = `normal`. */
  voz?: Voz;
}

export const PREFERENCIAS_PADRAO: PreferenciasA11y = {
  contraste: 'padrao',
  fonte: 'padrao',
  movimento: 'padrao',
};

export const ROTULO_FONTE: Record<Fonte, string> = {
  padrao: 'Padrão',
  grande: 'Grande',
  muito_grande: 'Muito grande',
};

export const ROTULO_CONTRASTE: Record<Contraste, string> = {
  padrao: 'Padrão',
  alto: 'Alto contraste',
};

export const ROTULO_MOVIMENTO: Record<Movimento, string> = {
  padrao: 'Com animações',
  reduzido: 'Reduzir animações',
};

export const ROTULO_VOZ: Record<Voz, string> = {
  normal: 'Microfone quando eu tocar',
  ler: 'Ler as respostas em voz alta',
  conversa: 'Conversar por voz',
};

export const CHAVE_LOCAL = 'chamaum:a11y';

export function ehPreferenciasValidas(v: unknown): v is PreferenciasA11y {
  if (typeof v !== 'object' || v === null) return false;
  const o = v as Record<string, unknown>;
  return (
    CONTRASTES.includes(o['contraste'] as Contraste) &&
    FONTES.includes(o['fonte'] as Fonte) &&
    MOVIMENTOS.includes(o['movimento'] as Movimento) &&
    (o['voz'] === undefined || VOZES.includes(o['voz'] as Voz))
  );
}
